import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchContentList, toggleContentFavorite, toggleContentReaction, IContent, IFetchContentListParams } from 'api/student/v2/content.api';
import { getKoachList, toggleKoachFollow } from 'api/student/v2/koach.api';
import { IPagination } from 'api/interfaces/common.interface';
import { getCurrentLanguageCode } from 'helpers/String.helper';
import VideoPlayer from 'commons/video/VideoPlayer';
import { getImageUrl } from 'helpers/Image.helper';
import { formatTimeAgo } from 'helpers/Datetime.helper';
import { getLanguageValue } from 'locales/i18n';
import { PAGE_TYPE } from 'enums';
import { usePopup } from 'contexts/PopupContext';
import ContentCommentPopup from '../../popup/ContentCommentPopup';
import ContentDetailPopup from '../../popup/ContentDetailPopup';
import KoachInfoPopup from '../../popup/KoachInfoPopup';

const GrowthContents = () => {
    const navigate = useNavigate();
    const { showPopup } = usePopup();
    
    // 무지개 애니메이션 스타일
    const rainbowStyle = `
        @keyframes rainbow {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
        }
    `;
    const [contents, setContents] = useState<IContent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [favoritingContentIds, setFavoritingContentIds] = useState<Set<number>>(new Set());
    const [reactingContentIds, setReactingContentIds] = useState<Set<number>>(new Set());
    const [showReactionTooltip, setShowReactionTooltip] = useState<number | null>(null);
    const [pagination, setPagination] = useState<IPagination>({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        hasNextPage: false,
        hasPrevPage: false
    });
    const [koachList, setKoachList] = useState<Array<{
        id: number;
        nickname?: string;
        profileImageUrl?: string;
        profileImage?: any;
        isFollowing?: boolean;
        hasNewContent?: boolean;
        latestContentId?: number;
    }>>([]);
    const [koachListLoading, setKoachListLoading] = useState(true);
    const [followingKoachIds, setFollowingKoachIds] = useState<Set<number>>(new Set());
    const [followingKoachLoadingIds, setFollowingKoachLoadingIds] = useState<Set<number>>(new Set());
    const [expandedContentIds, setExpandedContentIds] = useState<Set<number>>(new Set());
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const isLoadingRef = useRef(false); // 중복 호출 방지를 위한 ref

    const loadContents = useCallback(async (page: number = 1, append: boolean = false) => {
        // 이미 로딩 중이면 중복 호출 방지
        if (isLoadingRef.current) {
            console.log('이미 로딩 중입니다. 중복 호출을 방지합니다.');
            return;
        }

        try {
            isLoadingRef.current = true;
            if (append) {
                setIsLoadingMore(true);
            } else {
                setLoading(true);
            }
            setError(null);
            
            const params: IFetchContentListParams = {
                page,
                limit: 20
            };

            const response = await fetchContentList(params);
            
            if (append) {
                // 무한 스크롤: 기존 콘텐츠에 추가
                setContents(prev => [...prev, ...response.contentList]);
            } else {
                // 초기 로드: 콘텐츠 교체
                setContents(response.contentList);
            }
            
            // API 응답의 pagination을 IPagination 형식으로 변환
            const apiPagination = response.pagination as any;
            setPagination({
                currentPage: apiPagination.currentPage || 1,
                totalPages: apiPagination.totalPages || 1,
                totalCount: apiPagination.totalItems || 0,
                hasNextPage: (apiPagination.currentPage || 1) < (apiPagination.totalPages || 1),
                hasPrevPage: (apiPagination.currentPage || 1) > 1
            });
        } catch (err: any) {
            console.error('컨텐츠 목록 조회 실패:', err);
            setError(err.message || '컨텐츠를 불러오는데 실패했습니다.');
            if (!append) {
                setContents([]);
            }
        } finally {
            setLoading(false);
            setIsLoadingMore(false);
            isLoadingRef.current = false;
        }
    }, []);

    // 코치 목록 불러오기
    const loadKoachList = useCallback(async () => {
        try {
            setKoachListLoading(true);
            const response = await getKoachList(100);
            const koaches = response.koachList.map(koach => ({
                id: koach.id,
                nickname: koach.nickname,
                profileImage: koach.profileImage,
                isFollowing: koach.isFollowing || false,
                hasNewContent: koach.hasNewContent || false,
                latestContentId: koach.latestContentId
            }));
            setKoachList(koaches);
            setFollowingKoachIds(new Set(koaches.filter(k => k.isFollowing).map(k => k.id)));
        } catch (err: any) {
            console.error('코치 목록 조회 실패:', err);
        } finally {
            setKoachListLoading(false);
        }
    }, []);

    // 코치 팔로우 토글
    const handleKoachFollowToggle = async (koachId: number, event: React.MouseEvent) => {
        event.stopPropagation(); // 코치 클릭 이벤트 방지
        
        if (followingKoachIds.has(koachId) || followingKoachLoadingIds.has(koachId)) return; // 이미 팔로우 중이거나 로딩 중이면 무시
        
        try {
            setFollowingKoachLoadingIds(prev => new Set(prev).add(koachId));
            const result = await toggleKoachFollow(koachId.toString());
            
            const newIsFollowing = (result as any)?.data?.isFollowing ?? result?.isFollowing;
            
            setKoachList(prevList => 
                prevList.map(koach => 
                    koach.id === koachId 
                        ? { ...koach, isFollowing: newIsFollowing }
                        : koach
                )
            );
            
            if (newIsFollowing) {
                setFollowingKoachIds(prev => new Set(prev).add(koachId));
            } else {
                setFollowingKoachIds(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(koachId);
                    return newSet;
                });
            }
        } catch (error) {
            console.error('팔로우 토글 실패:', error);
        } finally {
            setFollowingKoachLoadingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(koachId);
                return newSet;
            });
        }
    };

    useEffect(() => {
        loadContents(1);
        loadKoachList();
    }, [loadContents, loadKoachList]);

    // 무한 스크롤 처리
    useEffect(() => {
        const handleScroll = () => {
            // 이미 로딩 중이면 스크롤 이벤트 무시
            if (isLoadingRef.current) {
                return;
            }

            // 스크롤이 하단 근처에 도달했는지 확인
            const scrollHeight = document.documentElement.scrollHeight;
            const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
            const clientHeight = document.documentElement.clientHeight;

            // 하단에서 200px 전에 도달하면 다음 페이지 로드
            if (scrollHeight - scrollTop - clientHeight < 200) {
                if (pagination.hasNextPage && !isLoadingMore && !loading) {
                    loadContents(pagination.currentPage + 1, true);
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [pagination.hasNextPage, isLoadingMore, loading, pagination.currentPage, loadContents]);

    const handleContentClick = (contentId: number) => {
        // 상세 페이지로 이동 (나중에 구현)
        // navigate(`/student/growth/contents/${contentId}`);
    };

    // 코치 프로필 클릭 핸들러
    const handleKoachClick = (content: IContent, event: React.MouseEvent) => {
        event.stopPropagation(); // 콘텐츠 클릭 이벤트 방지
        
        const koachId = content.user?.userKoach?.id;
        if (!koachId) {
            console.warn('코치 정보가 없습니다.');
            return;
        }

        const popupContent = (
            <KoachInfoPopup
                koachId={koachId.toString()}
                onClose={() => showPopup(null)}
            />
        );
        showPopup(popupContent);
    };

    // 댓글 버튼 클릭 핸들러
    const handleCommentClick = (content: IContent, event: React.MouseEvent) => {
        event.stopPropagation(); // 콘텐츠 클릭 이벤트 방지
        
        const popupContent = (
            <ContentCommentPopup
                contentId={content.id}
                contentTitle={getTitle(content)}
                contentDesc={getDesc(content)}
                onClose={() => showPopup(null)}
                onRefresh={() => loadContents(pagination.currentPage)}
            />
        );
        showPopup(popupContent);
    };

    // 리액션 버튼 클릭 핸들러
    const handleReactionClick = (contentId: number, event: React.MouseEvent) => {
        event.stopPropagation(); // 콘텐츠 클릭 이벤트 방지
        setShowReactionTooltip(showReactionTooltip === contentId ? null : contentId);
    };

    // 리액션 선택 핸들러
    const handleReactionSelect = async (contentId: number, reactionType: string, event: React.MouseEvent) => {
        event.stopPropagation();
        setShowReactionTooltip(null);
        
        // 이미 리액션 처리 중인 콘텐츠인지 확인
        if (reactingContentIds.has(contentId)) {
            console.log("🚫 이미 리액션 처리 중인 콘텐츠:", contentId);
            return;
        }

        try {
            // 리액션 처리 중인 콘텐츠 ID 추가
            setReactingContentIds(prev => new Set(prev).add(contentId));
            console.log("⭐ 리액션 토글 시작:", contentId, reactionType);

            const result = await toggleContentReaction(contentId, reactionType);
            console.log("리액션 토글 결과:", result);

            // 콘텐츠 목록에서 해당 콘텐츠의 리액션 수 및 상태 업데이트
            setContents(prevList =>
                prevList.map(item =>
                    item.id === contentId
                        ? {
                            ...item,
                            reactionCount: result.reactionCount,
                            myReactionTypeCode: result.reactionTypeCode
                        }
                        : item
                )
            );

        } catch (error) {
            console.error("리액션 토글 실패:", error);
            // 에러 처리 (필요시 사용자에게 알림)
        } finally {
            // 리액션 처리 완료 후 ID 제거
            setReactingContentIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(contentId);
                return newSet;
            });
            console.log("✅ 리액션 토글 완료:", contentId);
        }
    };

    // 외부 클릭 시 툴팁 닫기
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (showReactionTooltip !== null) {
                setShowReactionTooltip(null);
            }
        };
        
        if (showReactionTooltip !== null) {
            document.addEventListener('click', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showReactionTooltip]);

    // 즐겨찾기 토글 처리
    const handleFavoriteToggle = async (content: IContent, event: React.MouseEvent) => {
        event.stopPropagation(); // 콘텐츠 클릭 이벤트 방지

        // 이미 즐겨찾기 처리 중인 콘텐츠인지 확인
        if (favoritingContentIds.has(content.id)) {
            console.log("🚫 이미 즐겨찾기 처리 중인 콘텐츠:", content.id);
            return;
        }

        try {
            // 즐겨찾기 처리 중인 콘텐츠 ID 추가
            setFavoritingContentIds(prev => new Set(prev).add(content.id));
            console.log("⭐ 즐겨찾기 토글 시작:", content.id);

            const result = await toggleContentFavorite(content.id);
            console.log("즐겨찾기 토글 결과:", result);

            // 콘텐츠 목록에서 해당 콘텐츠의 즐겨찾기 상태와 수 업데이트
            setContents(prevList =>
                prevList.map(item =>
                    item.id === content.id
                        ? {
                            ...item,
                            isFavorite: result.isFavorite,
                            favoriteCount: result.favoriteCount
                        }
                        : item
                )
            );

        } catch (error) {
            console.error("즐겨찾기 토글 실패:", error);
            // 에러 처리 (필요시 사용자에게 알림)
        } finally {
            // 즐겨찾기 처리 완료 후 ID 제거
            setFavoritingContentIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(content.id);
                return newSet;
            });
            console.log("✅ 즐겨찾기 토글 완료:", content.id);
        }
    };

    const getTitle = (content: IContent): string => {
        // 기본 제목 사용, 없으면 번역에서 가져오기
        if (content.title) return content.title;
        if (content.multiTexts && content.multiTexts.length > 0) {
            const currentLang = getCurrentLanguageCode();
            const currentMultiText = content.multiTexts.find(mt => mt.languageCode === currentLang);
            return currentMultiText?.title || content.multiTexts[0]?.title || '제목 없음';
        }
        return '제목 없음';
    };

    const getDesc = (content: IContent): string => {
        // 기본 설명 사용, 없으면 번역에서 가져오기
        if (content.desc) return content.desc;
        if (content.multiTexts && content.multiTexts.length > 0) {
            const currentLang = getCurrentLanguageCode();
            const currentMultiText = content.multiTexts.find(mt => mt.languageCode === currentLang);
            return currentMultiText?.desc || content.multiTexts[0]?.desc || '';
        }
        return '';
    };

    const getKoachName = (content: IContent): string => {
        if (content.user?.userKoach?.nickname) {
            return content.user.userKoach.nickname;
        }
        if (content.user?.username) {
            return content.user.username;
        }
        return '코치';
    };

    const getKoachProfileImage = (content: IContent): string => {
        const userKoach = content.user?.userKoach;
        if (!userKoach) {
            return '/imgs/dummy/basic-img.png';
        }

        // profileImageInfo가 있는 경우 우선 사용 (KoachInfoPopup 패턴 참고)
        const userKoachWithImageInfo = userKoach as typeof userKoach & {
            profileImageInfo?: { id: string; url: string };
        };
        
        // 1순위: profileImageInfo.url (백엔드에서 생성된 URL)
        if (userKoachWithImageInfo.profileImageInfo?.url) {
            return userKoachWithImageInfo.profileImageInfo.url;
        }
        // 2순위: profileImageUrl (백엔드에서 생성된 URL)
        if (userKoach.profileImageUrl) {
            return userKoach.profileImageUrl;
        }
        // 3순위: profileImage 객체를 getImageUrl로 처리 (KoachInfoPopup 패턴)
        if (userKoach.profileImage) {
            return getImageUrl(userKoach.profileImage, 200, 200);
        }
        // 모든 경우에 실패하면 기본 이미지 반환
        return '/imgs/dummy/basic-img.png';
    };

    if (error && contents.length === 0) {
        return (
            <main className="bg-gray-50 min-h-screen">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex justify-center items-center h-64">
                        <div className="text-red-500">{error}</div>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main className="bg-gray-50 min-h-screen">
            <style>{rainbowStyle}</style>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* 헤더 - 항상 표시 */}
                <div className="mb-8">
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                        {getLanguageValue(PAGE_TYPE.STUDENT, "growthContents.txtTitle") || "코치 콘텐츠"}
                    </h1>
                    <p className="text-gray-600 text-sm sm:text-base">
                        {getLanguageValue(PAGE_TYPE.STUDENT, "growthContents.txtDesc") || "코치들이 만든 콘텐츠를 확인하세요"}
                    </p>
                </div>
                
                {/* 코치 목록 */}
                <div className="mb-8">
                    {koachListLoading ? (
                        <div className="overflow-x-auto scrollbar-hide">
                            <div className="flex gap-0.5">
                                {/* 코치 목록 스켈레톤 */}
                                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                                    <div key={i} className="w-[80px] sm:w-[90px] lg:w-[100px] flex-shrink-0">
                                        <div className="flex flex-col items-center">
                                            <div className="rounded-full w-14 h-14 sm:w-16 sm:h-16 bg-gray-200 animate-pulse aspect-square"></div>
                                            <div className="mt-2 w-full">
                                                <div className="h-3 bg-gray-200 rounded animate-pulse mx-auto w-3/4"></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : koachList.length > 0 ? (
                        <div className="overflow-x-auto scrollbar-hide">
                            <div className="flex gap-0.5">
                                {koachList.map((koach) => {
                                    // API에서 받은 하루 이내 콘텐츠 존재 여부 사용
                                    const hasNewContent = koach.hasNewContent || false;
                                    
                                    return (
                                        <div 
                                            key={koach.id} 
                                            className="w-[80px] sm:w-[90px] lg:w-[100px] flex-shrink-0 cursor-pointer"
                                            onClick={() => {
                                                // 하루 이내 콘텐츠가 있으면 최신 콘텐츠 팝업 열기
                                                if (hasNewContent && koach.latestContentId) {
                                                    const popupContent = (
                                                        <ContentDetailPopup
                                                            contentId={koach.latestContentId}
                                                            onClose={() => showPopup(null)}
                                                            onRefresh={() => loadContents(pagination.currentPage)}
                                                        />
                                                    );
                                                    showPopup(popupContent);
                                                } else {
                                                    // 없으면 코치 프로필 팝업 열기
                                                    const popupContent = (
                                                        <KoachInfoPopup
                                                            koachId={koach.id.toString()}
                                                            onClose={() => showPopup(null)}
                                                        />
                                                    );
                                                    showPopup(popupContent);
                                                }
                                            }}
                                        >
                                            <div className="flex flex-col items-center">
                                                <div className="relative rounded-full w-14 h-14 sm:w-16 sm:h-16 flex-shrink-0 aspect-square">
                                                    {/* 팔로우 중이 아닌 경우 + 아이콘 */}
                                                    {!koach.isFollowing && (
                                                        <button
                                                            onClick={(e) => handleKoachFollowToggle(koach.id, e)}
                                                            disabled={followingKoachLoadingIds.has(koach.id)}
                                                            className={`absolute top-0 right-0 w-5 h-5 bg-[#CF3688] text-white rounded-full flex items-center justify-center shadow-md hover:bg-[#B52E74] transition-all z-10 ${
                                                                followingKoachLoadingIds.has(koach.id) ? 'opacity-50 cursor-not-allowed' : ''
                                                            }`}
                                                            title="팔로우"
                                                        >
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                    {hasNewContent ? (
                                                        <div 
                                                            className="rounded-full p-[2px] w-full h-full aspect-square"
                                                            style={{
                                                                background: 'linear-gradient(45deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #9400d3)',
                                                                backgroundSize: '400% 400%',
                                                                animation: 'rainbow 3s ease infinite'
                                                            }}
                                                        >
                                                            <div className="rounded-full overflow-hidden w-full h-full bg-white aspect-square">
                                                                <img
                                                                    src={
                                                                        koach.profileImageUrl 
                                                                            || (koach.profileImage ? getImageUrl(koach.profileImage, 200, 200) : null)
                                                                            || '/imgs/dummy/basic-img.png'
                                                                    }
                                                                    alt={koach.nickname || '코치'}
                                                                    className="w-full h-full object-cover"
                                                                    onError={(e) => {
                                                                        const target = e.target as HTMLImageElement;
                                                                        target.src = '/imgs/dummy/basic-img.png';
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="rounded-full overflow-hidden w-full h-full border border-gray-100 aspect-square">
                                                            <img
                                                                src={
                                                                    koach.profileImageUrl 
                                                                        || (koach.profileImage ? getImageUrl(koach.profileImage, 200, 200) : null)
                                                                        || '/imgs/dummy/basic-img.png'
                                                                }
                                                                alt={koach.nickname || '코치'}
                                                                className="w-full h-full object-cover"
                                                                onError={(e) => {
                                                                    const target = e.target as HTMLImageElement;
                                                                    target.src = '/imgs/dummy/basic-img.png';
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="mt-2 text-center w-full overflow-hidden">
                                                    <p className="text-gray-900 text-xs truncate px-1">
                                                        {koach.nickname || '코치'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}
                </div>
                
                {/* 콘텐츠 목록 */}
                {loading ? (
                    <div className="space-y-6">
                        {/* 콘텐츠 목록 스켈레톤 */}
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="relative bg-white rounded-2xl shadow-sm overflow-hidden animate-pulse">
                                {/* 헤더 스켈레톤 */}
                                <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-2">
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <div className="h-4 bg-gray-200 rounded w-20"></div>
                                            <div className="h-3 bg-gray-200 rounded w-16"></div>
                                        </div>
                                        <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                                    </div>
                                </div>
                                {/* 콘텐츠 스켈레톤 */}
                                <div className="px-4 sm:px-6 pt-2 pb-4 sm:pb-6">
                                    <div className="space-y-2 mb-4">
                                        <div className="h-3 bg-gray-200 rounded w-full"></div>
                                        <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                                        <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                                    </div>
                                    <div className="h-40 bg-gray-200 rounded-lg mb-4"></div>
                                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                                        <div className="flex items-center space-x-4">
                                            <div className="h-5 bg-gray-200 rounded w-12"></div>
                                            <div className="h-5 bg-gray-200 rounded w-12"></div>
                                        </div>
                                        <div className="h-5 bg-gray-200 rounded w-12"></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : contents.length === 0 ? (
                    /* 빈 상태 화면 */
                    <div className="flex flex-col items-center justify-center py-16 px-4">
                        <div className="text-center max-w-md">
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">
                                등록된 컨텐츠가 없습니다
                            </h3>
                            <p className="text-gray-500 text-sm leading-relaxed">
                                코치들이 콘텐츠를 등록하면 여기에 표시됩니다.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {contents.map((content) => (
                            <div
                                key={content.id}
                                className="relative bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer"
                                onClick={() => handleContentClick(content.id)}
                            >
                                {/* 콘텐츠 헤더 */}
                                <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-2">
                                    <div className="space-y-2">
                                        {/* 첫 번째 줄: 코치 정보와 날짜 */}
                                        <div className="flex items-center justify-between">
                                            <button 
                                                className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
                                                onClick={(e) => handleKoachClick(content, e)}
                                            >
                                                <img 
                                                    src={getKoachProfileImage(content)} 
                                                    alt={getKoachName(content)}
                                                    className="w-9 h-9 rounded-full object-cover"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.src = '/imgs/dummy/basic-img.png';
                                                    }}
                                                />
                                                <span className="text-sm text-gray-600 hover:text-[#CF3688] transition-colors">{getKoachName(content)}</span>
                                            </button>
                                            <p className="text-sm text-gray-500">
                                                {formatTimeAgo(content.createdAt)}
                                            </p>
                                        </div>

                                        {/* 두 번째 줄: 제목 */}
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-lg sm:text-xl font-bold text-gray-900 break-words overflow-hidden">
                                                {getTitle(content)}
                                            </h3>
                                        </div>
                                    </div>
                                </div>

                        {/* 콘텐츠 내용 */}
                        <div className="px-4 sm:px-6 pt-2 pb-4 sm:pb-6">
                            {/* 비디오 */}
                            {content.video && (
                                <div className="mb-4">
                                    <VideoPlayer
                                        video={content.video}
                                        className="w-full rounded-lg border border-gray-200"
                                        enableVideoLog={false}
                                    />
                                </div>
                            )}

                            {/* 설명 */}
                            {getDesc(content) && (() => {
                                const desc = getDesc(content);
                                const isExpanded = expandedContentIds.has(content.id);
                                const shouldShowToggle = desc.split('\n').length > 4 || desc.length > 200;
                                
                                return (
                                    <div className="mb-4">
                                        <p className={`text-gray-700 text-sm sm:text-base leading-relaxed whitespace-pre-wrap break-words ${
                                            !isExpanded && shouldShowToggle ? 'line-clamp-4' : ''
                                        }`}>
                                            {desc}
                                        </p>
                                        {shouldShowToggle && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setExpandedContentIds(prev => {
                                                        const newSet = new Set(prev);
                                                        if (newSet.has(content.id)) {
                                                            newSet.delete(content.id);
                                                        } else {
                                                            newSet.add(content.id);
                                                        }
                                                        return newSet;
                                                    });
                                                }}
                                                className="mt-2 text-sm text-[#CF3688] hover:text-[#B52E74] transition-colors"
                                            >
                                                {isExpanded ? '접기' : '더보기'}
                                            </button>
                                        )}
                                    </div>
                                );
                            })()}

                                    {/* 하단 액션 바: Reaction, 댓글, 즐겨찾기 */}
                                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                                        {/* 왼쪽: Reaction과 댓글 */}
                                        <div className="flex items-center space-x-4">
                                            {/* Reaction */}
                                            <div className="relative">
                                                <button
                                                    onClick={(e) => handleReactionClick(content.id, e)}
                                                    className={`flex items-center space-x-1 transition-colors cursor-pointer ${
                                                        content.myReactionTypeCode 
                                                            ? 'text-[#CF3688]' 
                                                            : 'text-gray-500 hover:text-[#CF3688]'
                                                    }`}
                                                >
                                                    {content.myReactionTypeCode ? (
                                                        <img 
                                                            src={
                                                                content.myReactionTypeCode === 'reactionType:veryHelpful'
                                                                    ? '/imgs/icons/icon-reaction-very-helpful-active.svg'
                                                                    : content.myReactionTypeCode === 'reactionType:helpful'
                                                                    ? '/imgs/icons/icon-reaction-helpful-active.svg'
                                                                    : content.myReactionTypeCode === 'reactionType:disappointed'
                                                                    ? '/imgs/icons/icon-reaction-disappointed-active.svg'
                                                                    : '/imgs/icons/icon-reaction-very-helpful.svg'
                                                            }
                                                            alt="리액션"
                                                            className="w-5 h-5"
                                                        />
                                                    ) : (
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                    )}
                                                    <span className="text-sm font-medium">
                                                        {content.reactionCount || 0}
                                                    </span>
                                                </button>
                                                
                                                {/* 리액션 선택 툴팁 */}
                                                {showReactionTooltip === content.id && (
                                                    <div 
                                                        className="absolute bottom-full left-0 mb-2 flex items-center gap-1 bg-white border border-gray-200 rounded-lg shadow-lg p-1 z-10"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <button
                                                            onClick={(e) => handleReactionSelect(content.id, 'reactionType:veryHelpful', e)}
                                                            className={`flex items-center justify-center w-10 h-10 rounded transition-colors ${
                                                                content.myReactionTypeCode === 'reactionType:veryHelpful'
                                                                    ? 'bg-[#CF3688]/10'
                                                                    : 'hover:bg-gray-100'
                                                            }`}
                                                            title="큰 도움이 됐어요"
                                                        >
                                                            <img 
                                                                src={content.myReactionTypeCode === 'reactionType:veryHelpful' 
                                                                    ? '/imgs/icons/icon-reaction-very-helpful-active.svg'
                                                                    : '/imgs/icons/icon-reaction-very-helpful.svg'
                                                                }
                                                                alt="큰 도움이 됐어요"
                                                                className="w-6 h-6"
                                                            />
                                                        </button>
                                                        <button
                                                            onClick={(e) => handleReactionSelect(content.id, 'reactionType:helpful', e)}
                                                            className={`flex items-center justify-center w-10 h-10 rounded transition-colors ${
                                                                content.myReactionTypeCode === 'reactionType:helpful'
                                                                    ? 'bg-[#CF3688]/10'
                                                                    : 'hover:bg-gray-100'
                                                            }`}
                                                            title="도움이 됐어요"
                                                        >
                                                            <img 
                                                                src={content.myReactionTypeCode === 'reactionType:helpful' 
                                                                    ? '/imgs/icons/icon-reaction-helpful-active.svg'
                                                                    : '/imgs/icons/icon-reaction-helpful.svg'
                                                                }
                                                                alt="도움이 됐어요"
                                                                className="w-6 h-6"
                                                            />
                                                        </button>
                                                        <button
                                                            onClick={(e) => handleReactionSelect(content.id, 'reactionType:disappointed', e)}
                                                            className={`flex items-center justify-center w-10 h-10 rounded transition-colors ${
                                                                content.myReactionTypeCode === 'reactionType:disappointed'
                                                                    ? 'bg-[#CF3688]/10'
                                                                    : 'hover:bg-gray-100'
                                                            }`}
                                                            title="아쉬웠어요"
                                                        >
                                                            <img 
                                                                src={content.myReactionTypeCode === 'reactionType:disappointed' 
                                                                    ? '/imgs/icons/icon-reaction-disappointed-active.svg'
                                                                    : '/imgs/icons/icon-reaction-disappointed.svg'
                                                                }
                                                                alt="아쉬웠어요"
                                                                className="w-6 h-6"
                                                            />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {/* 댓글 */}
                                            <button
                                                onClick={(e) => handleCommentClick(content, e)}
                                                className="flex items-center space-x-1 text-gray-500 hover:text-[#CF3688] transition-colors cursor-pointer"
                                            >
                                                <img 
                                                    src="/imgs/icons/icon-comment.svg" 
                                                    alt="댓글" 
                                                    className="w-5 h-5"
                                                />
                                                <span className="text-sm font-medium">
                                                    {content.commentCount || 0}
                                                </span>
                                            </button>
                                        </div>

                                        {/* 오른쪽: 즐겨찾기 */}
                                        <button
                                            onClick={(e) => handleFavoriteToggle(content, e)}
                                            disabled={favoritingContentIds.has(content.id)}
                                            className={`flex items-center space-x-1 transition-all duration-200 ${
                                                favoritingContentIds.has(content.id) 
                                                    ? 'opacity-50 cursor-not-allowed' 
                                                    : content.isFavorite 
                                                        ? 'text-[#CF3688]' 
                                                        : 'text-gray-500'
                                            }`}
                                        >
                                            {favoritingContentIds.has(content.id) ? (
                                                // 로딩 중일 때 스피너 표시
                                                <svg className="w-5 h-5 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                            ) : (
                                                // 즐겨찾기 아이콘
                                                <img
                                                    src={content.isFavorite ? "/imgs/icons/icon-favorite-active.svg" : "/imgs/icons/icon-favorite.svg"}
                                                    alt="즐겨찾기"
                                                    className="w-5 h-5"
                                                />
                                            )}
                                            <span className="text-sm font-medium">
                                                {content.favoriteCount || 0}
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* 무한 스크롤 로딩 스켈레톤 */}
                        {isLoadingMore && (
                            <div className="space-y-6">
                                {[1, 2, 3].map((i) => (
                                    <div
                                        key={`skeleton-${i}`}
                                        className="bg-white rounded-2xl shadow-sm overflow-hidden"
                                    >
                                        {/* 콘텐츠 스켈레톤 */}
                                        <div className="px-4 sm:px-6 pt-2 pb-4 sm:pb-6">
                                            <div className="space-y-2 mb-4">
                                                <div className="h-3 bg-gray-200 rounded w-full animate-pulse"></div>
                                                <div className="h-3 bg-gray-200 rounded w-5/6 animate-pulse"></div>
                                                <div className="h-3 bg-gray-200 rounded w-4/6 animate-pulse"></div>
                                            </div>
                                            <div className="h-40 bg-gray-200 rounded-lg mb-4 animate-pulse"></div>
                                            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                                                <div className="flex items-center space-x-4">
                                                    <div className="h-5 bg-gray-200 rounded w-12 animate-pulse"></div>
                                                    <div className="h-5 bg-gray-200 rounded w-12 animate-pulse"></div>
                                                </div>
                                                <div className="h-5 bg-gray-200 rounded w-12 animate-pulse"></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* 더 이상 로드할 콘텐츠가 없을 때 */}
                        {!pagination.hasNextPage && contents.length > 0 && !isLoadingMore && (
                            <div className="flex justify-center items-center py-8">
                                <p className="text-gray-500 text-sm">모든 콘텐츠를 불러왔습니다.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </main>
    );
};

export default GrowthContents;
